import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { groupsAPI } from '@/lib/api';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from 'sonner';
import { Search, Loader2, Users, Plus, Globe, Lock, X } from 'lucide-react';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';

const CATEGORIES = [
  { value: 'geral', label: 'Apoio Geral' },
  { value: 'familia', label: 'Família em Recuperação' },
  { value: 'alcool', label: 'Álcool e Outras Drogas' },
  { value: 'jogos-apostas', label: 'Apostas e Bets' },
  { value: 'alimentar', label: 'Compulsão Alimentar' },
  { value: 'nicotina', label: 'Tabagismo' },
  { value: 'emocoes', label: 'Saúde Mental' },
  { value: 'profissional', label: 'Reintegração Profissional' },
  { value: 'drogas', label: 'Drogas' },
  { value: 'cocaina', label: 'Cocaína' },
  { value: 'sexo-afetividade', label: 'Sexo e Afetividade' },
  { value: 'lgbt', label: 'LGBT+' },
  { value: 'outro', label: 'Outra' },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

function getCategoryLabel(cat) {
  if (!cat) return 'Geral';
  return CATEGORY_MAP[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
}

export function Groups() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [exploreGroups, setExploreGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [exploreLoading, setExploreLoading] = useState(true);
  const [myGroupsLoading, setMyGroupsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 300);

  useEffect(() => { loadExploreGroups(); }, [debouncedSearch]);
  useEffect(() => { loadMyGroups(); }, []);

  const loadExploreGroups = async () => {
    setExploreLoading(true);
    try {
      const res = await groupsAPI.getGroups({ q: debouncedSearch });
      setExploreGroups(res.data || []);
    } catch { toast.error('Erro ao carregar grupos'); }
    finally { setExploreLoading(false); }
  };

  const loadMyGroups = async () => {
    setMyGroupsLoading(true);
    try {
      const res = await groupsAPI.getMyGroups();
      setMyGroups(res.data || []);
    } catch { toast.error('Erro ao carregar seus grupos'); }
    finally { setMyGroupsLoading(false); }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await groupsAPI.joinGroup(groupId);
      toast.success('Você entrou no grupo!');
      loadExploreGroups();
      loadMyGroups();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao entrar no grupo');
    }
  };

  const handleGroupCreated = () => {
    setShowCreateModal(false);
    loadExploreGroups();
    loadMyGroups();
  };

  return (
    <PageTransition>
      <div className="max-w-5xl mx-auto" data-testid="groups-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Grupos</h1>
            <p className="text-sm text-muted-foreground">Conecte-se com pessoas que compartilham a mesma jornada</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="rounded-xl gap-2 self-start sm:self-auto"
            data-testid="create-group-button"
          >
            <Plus className="h-4 w-4" />
            Criar Grupo
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar grupos..."
            className="pl-10 h-11 rounded-xl"
            data-testid="search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="explore" className="w-full">
          <TabsList className="mb-5 rounded-xl">
            <TabsTrigger value="explore" className="rounded-lg" data-testid="explore-tab">Explorar</TabsTrigger>
            <TabsTrigger value="my-groups" className="rounded-lg" data-testid="my-groups-tab">
              Meus Grupos{myGroups.length > 0 && ` (${myGroups.length})`}
            </TabsTrigger>
          </TabsList>

          {/* Explore */}
          <TabsContent value="explore" data-testid="explore-content">
            {exploreLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
            ) : exploreGroups.length === 0 ? (
              <EmptyState
                title={searchTerm ? 'Nenhum grupo encontrado' : 'Nenhum grupo disponível'}
                subtitle={searchTerm ? 'Tente outro termo de busca' : 'Seja o primeiro a criar um grupo!'}
                onAction={() => setShowCreateModal(true)}
                actionLabel="Criar Grupo"
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="explore-groups-grid">
                {exploreGroups.map((g, i) => (
                  <FadeInItem key={g.id} index={i}>
                    <GroupCardSimple group={g} onJoin={handleJoinGroup} />
                  </FadeInItem>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Groups */}
          <TabsContent value="my-groups" data-testid="my-groups-content">
            {myGroupsLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
            ) : myGroups.length === 0 ? (
              <EmptyState title="Você ainda não participa de nenhum grupo" subtitle="Explore e encontre comunidades que combinem com você" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-groups-grid">
                {myGroups.map((g, i) => (
                  <FadeInItem key={g.id} index={i}>
                    <GroupCardSimple group={g} />
                  </FadeInItem>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Group Modal */}
        <CreateGroupModal
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
          onCreated={handleGroupCreated}
        />
      </div>
    </PageTransition>
  );
}

/* ═══ Group Card (simplified) ═══ */
function GroupCardSimple({ group, onJoin }) {
  const navigate = useNavigate();

  const handleView = () => navigate(`/grupos/${group.slug}`);
  const handleJoin = async (e) => {
    e.stopPropagation();
    if (onJoin) await onJoin(group.id);
  };

  return (
    <Card
      className="border-border/40 shadow-sm hover:shadow-md transition-all cursor-pointer rounded-2xl overflow-hidden"
      onClick={handleView}
      data-testid={`group-card-${group.slug}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
            <Users className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>

          <div className="flex-1 min-w-0">
            {/* Name */}
            <h3 className="font-semibold text-sm truncate" data-testid="group-name">{group.name}</h3>

            {/* Category + Open/Closed */}
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 rounded-md" data-testid="group-category">
                {getCategoryLabel(group.category)}
              </Badge>
              {group.is_public === false && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 rounded-md gap-0.5" data-testid="group-closed-badge">
                  <Lock className="h-2.5 w-2.5" /> Fechado
                </Badge>
              )}
            </div>

            {/* Members */}
            <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1" data-testid="group-member-count">
              <Users className="h-3 w-3" />
              {group.member_count} {group.member_count === 1 ? 'membro' : 'membros'}
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-3">
          {group.is_member ? (
            <Button variant="outline" size="sm" className="w-full rounded-xl text-xs h-8" onClick={(e) => { e.stopPropagation(); handleView(); }} data-testid="view-group-button">
              Ver Grupo
            </Button>
          ) : (
            <Button size="sm" className="w-full rounded-xl text-xs h-8" onClick={handleJoin} data-testid="join-group-button">
              Entrar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══ Empty State ═══ */
function EmptyState({ title, subtitle, onAction, actionLabel }) {
  return (
    <div className="text-center py-16" data-testid="groups-empty-state">
      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
        <Users className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <h3 className="font-semibold mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>
      {onAction && (
        <Button onClick={onAction} variant="outline" className="rounded-xl" data-testid="empty-create-group-btn">
          <Plus className="h-4 w-4 mr-2" />{actionLabel}
        </Button>
      )}
    </div>
  );
}

/* ═══ Create Group Modal ═══ */
function CreateGroupModal({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const resetForm = () => {
    setName(''); setDescription(''); setCategory(''); setVisibility('public'); setErrors({});
  };

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Nome é obrigatório';
    else if (name.length < 3 || name.length > 60) errs.name = 'Nome deve ter entre 3 e 60 caracteres';
    if (!category) errs.category = 'Categoria é obrigatória';
    if (description.length > 400) errs.description = 'Máximo 400 caracteres';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await groupsAPI.createGroup({
        name: name.trim(),
        category,
        description: description.trim(),
        visibility,
      });
      toast.success('Grupo criado com sucesso!');
      resetForm();
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar grupo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="create-group-modal">
        <DialogHeader>
          <DialogTitle>Criar Grupo</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Crie um grupo de apoio para a comunidade U-Clean.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div>
            <Label htmlFor="group-name" className="text-sm font-medium">Nome do Grupo *</Label>
            <Input
              id="group-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex: Apoio Mútuo - Álcool"
              className="mt-1.5 rounded-xl"
              data-testid="create-group-name"
            />
            {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
          </div>

          {/* Category */}
          <div>
            <Label className="text-sm font-medium">Categoria *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="create-group-category">
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && <p className="text-xs text-destructive mt-1">{errors.category}</p>}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="group-desc" className="text-sm font-medium">Descrição curta</Label>
            <Textarea
              id="group-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Descreva brevemente o propósito do grupo..."
              rows={3}
              className="mt-1.5 rounded-xl resize-none"
              data-testid="create-group-description"
            />
            <p className="text-[10px] text-muted-foreground mt-1 text-right">{description.length}/400</p>
            {errors.description && <p className="text-xs text-destructive mt-1">{errors.description}</p>}
          </div>

          {/* Visibility */}
          <div>
            <Label className="text-sm font-medium">Tipo</Label>
            <RadioGroup value={visibility} onValueChange={setVisibility} className="mt-2 flex gap-4">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="public" id="v-public" data-testid="visibility-public" />
                <Label htmlFor="v-public" className="flex items-center gap-1.5 text-sm font-normal cursor-pointer">
                  <Globe className="h-3.5 w-3.5 text-green-500" /> Aberto
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="private" id="v-private" data-testid="visibility-private" />
                <Label htmlFor="v-private" className="flex items-center gap-1.5 text-sm font-normal cursor-pointer">
                  <Lock className="h-3.5 w-3.5 text-amber-500" /> Fechado
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Cancelar</Button>
          <Button onClick={handleCreate} disabled={loading} className="rounded-xl gap-2" data-testid="create-group-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Criar Grupo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
